import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Button, IconButton, Tooltip,
  Chip, Typography, alpha,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Cancel as CancelIcon,
  Event as EventIcon, EventAvailable as EventSidebarIcon, DateRange as DateRangeIcon, Visibility as VisibilityIcon,
  LocationOn as LocationIcon, Category as CategoryIcon,
  Schedule as ScheduleIcon, CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { formatEventTimeRange } from '../../../utils/malaysiaTime';
import { useTheme } from '@mui/material/styles';
import { eventService } from '../../../services/eventService';
import { useAuth } from '../../../contexts/AuthContext';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import ListFilterBar from '../../common/ListFilterBar';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { EVENT_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';
import api from '../../../services/api';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import FeedbackMessage from '../../common/FeedbackMessage';
import { canCreateEvent, canModifyEvent, canCancelEvent } from '../../../utils/eventPenaltyPermissions';
import { cancelViaBulkUpdate } from '../../../utils/resourceCancel';

// ─── Design tokens ────────────────────────────────────────────────────────────
const B = { 600: '#2563EB', 700: '#1D4ED8', 900: '#1E3A8A' };
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';
const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

// Readable text constants — prevents dark-on-dark issues
const TEXT_PRIMARY   = alpha('#E2E8F0', 0.88);
const TEXT_SECONDARY = alpha('#CBD5E1', 0.6);
const TEXT_MONO      = alpha(NEON, 0.55);

// ─── Type/Status neon maps ────────────────────────────────────────────────────
const TYPE_NEON   = { Demo: NEON, Prototype: NEON2, Training: NEON3, LabCleaning: '#F59E0B', Other: alpha(NEON, 0.45) };
const STATUS_NEON = { Completed: NEON3, 'In Progress': '#F59E0B', Pending: NEON, Cancelled: '#F87171' };

const NeonChip = ({ label, neon, icon }) => (
  <Chip
    label={label}
    icon={icon ? React.cloneElement(icon, { sx: { fontSize: '13px !important', color: `${neon} !important` } }) : undefined}
    size="small"
    sx={{
      height: 22,
      fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, letterSpacing: 0.4,
      background: `linear-gradient(135deg, ${alpha(neon, 0.18)}, ${alpha(B[700], 0.4)})`,
      color: neon, border: `1px solid ${alpha(neon, 0.32)}`,
      boxShadow: `0 0 7px ${alpha(neon, 0.2)}`,
      '& .MuiChip-label': { px: 1 },
    }}
  />
);

const EventList = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  
  const theme = useTheme();
  const [events,          setEvents]          = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [success,         setSuccess]         = useState(null);
  const [page,            setPage]            = useState(0);
  const [rowsPerPage,     setRowsPerPage]     = useState(10);
  const [searchTerm,      setSearchTerm]      = useState('');
  const [showFilters,     setShowFilters]     = useState(false);
  const [filters,         setFilters]         = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading,   setCancelLoading]   = useState(false);
  const [selectedEvent,   setSelectedEvent]   = useState(null);

  const filterOptions = [
    { name: 'type',       label: 'Event Type', type: 'select', options: [{ value: 'Demo', label: 'Demo' }, { value: 'Prototype', label: 'Prototype' }, { value: 'Training', label: 'Training' }, { value: 'LabCleaning', label: 'Lab Cleaning' }, { value: 'Other', label: 'Other' }] },
    { name: 'status',     label: 'Status',     type: 'select', options: [{ value: 'Pending', label: 'Pending' }, { value: 'In Progress', label: 'In Progress' }, { value: 'Completed', label: 'Completed' }, { value: 'Cancelled', label: 'Cancelled' }] },
    { name: 'date_range', label: 'Date Range', type: 'select', options: [{ value: 'today', label: 'Today' }, { value: 'this_week', label: 'This Week' }, { value: 'this_month', label: 'This Month' }, { value: 'upcoming', label: 'Upcoming' }, { value: 'past', label: 'Past Events' }] },
  ];

  useEffect(() => { if (!currentUser) return; fetchEvents(); }, [currentUser]);

  const fetchEvents = async ({ forceRefresh = false } = {}) => {
    try {
      setLoading(true);
      setEvents(await eventService.getAll({ forceRefresh }));
      setError(null);
    } catch { setError('Failed to fetch events'); }
    finally { setLoading(false); }
  };

  const isDateInRange = (eventDate, range) => {
    const today = dayjs(), eventDay = dayjs(eventDate);
    if (range === 'today')      return eventDay.isSame(today, 'day');
    if (range === 'this_week')  return eventDay.isSame(today, 'week');
    if (range === 'this_month') return eventDay.isSame(today, 'month');
    if (range === 'upcoming')   return eventDay.isAfter(today);
    if (range === 'past')       return eventDay.isBefore(today);
    return true;
  };

  const filteredEvents = events
    .filter((event) => {
      const isCancelled = String(event.status || '').toLowerCase() === 'cancelled';
      if (isCancelled && filters.status !== 'Cancelled') return false;

      const matchesSearch =
        event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.venue.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.remark && event.remark.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesFilters = Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        if (key === 'type')       return event.type === value;
        if (key === 'status')     return event.status === value;
        if (key === 'date_range') return isDateInRange(event.start_date, value);
        return true;
      });
      return matchesSearch && matchesFilters;
    })
    .sort((a, b) => {
      const sortRank = (status) => {
        const s = String(status).toLowerCase();
        if (s === 'cancelled') return 2;
        if (s === 'completed') return 1;
        return 0;
      };
      return sortRank(a.status) - sortRank(b.status);
    });

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    { icon: EventIcon,          value: events.length,                                                     label: 'Total Events', color: 'info'      },
    { icon: ScheduleIcon,       value: events.filter(e => dayjs(e.start_date).isAfter(dayjs())).length,  label: 'Upcoming',     color: 'success'   },
    { icon: HourglassEmptyIcon, value: events.filter(e => e.status === 'In Progress').length,            label: 'In Progress',  color: 'warning'   },
    { icon: CheckCircleIcon,    value: events.filter(e => e.status === 'Completed').length,              label: 'Completed',    color: 'secondary' },
  ];

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'upcoming', 'in-progress', 'completed'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    // Keep user-set type filter; only override the stat-driven filter dimensions.
    delete nextFilters.date_range;
    delete nextFilters.status;

    if (nextFilter === 'upcoming') nextFilters.date_range = 'upcoming';
    if (nextFilter === 'in-progress') nextFilters.status = 'In Progress';
    if (nextFilter === 'completed') nextFilters.status = 'Completed';

    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'upcoming'   ? 1 :
    activeStatFilter === 'in-progress' ? 2 :
    activeStatFilter === 'completed'  ? 3 :
    0;

  const handleCancel = (event) => {
    setSelectedEvent(event);
    setCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (!selectedEvent) return;

    try {
      setCancelLoading(true);
      await cancelViaBulkUpdate(EVENT_BULK_CONFIG.endpoint, selectedEvent.id, 'Cancelled');
      setEvents((prev) =>
        prev.map((e) => (e.id === selectedEvent.id ? { ...e, status: 'Cancelled' } : e)),
      );
      setCancelDialogOpen(false);
      setSelectedEvent(null);
      setSuccess(`Event "${selectedEvent.name}" has been cancelled.`);
    } catch (err) {
      console.error('Error cancelling event:', err);
      setError(err?.response?.data?.message || 'Failed to cancel event. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleBulkCancel = async () => {
    if (!selectedIds?.length) {
      setError('No selected events to cancel.');
      return;
    }

    try {
      await api.put(EVENT_BULK_CONFIG.endpoint, {
        ids: selectedIds.map(Number),
        status: 'Cancelled',
      });

      const selectedSet = new Set(selectedIds.map(String));
      setEvents((prev) => prev.map((row) => (
        selectedSet.has(String(row.id))
          ? { ...row, status: 'Cancelled' }
          : row
      )));
      setError(null);
      setSuccess(`${selectedIds.length} event(s) cancelled successfully.`);
      clearSelection();
      fetchEvents({ forceRefresh: true });
    } catch (err) {
      setError(
        err?.response?.data?.message
        || Object.values(err?.response?.data?.errors || {}).flat()?.[0]
        || 'Bulk cancel failed.',
      );
    }
  };

  // ── Table columns — all text explicitly set to light colors ──────────────
  const columns = [
    {
      key: 'name',
      label: 'Event',
      icon: EventIcon,
      primary: true,
      minWidth: 220,
      render: (event) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Neon icon box — replaces dark Avatar */}
          <Box sx={{
            width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
            background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B[700], 0.45)})`,
            border: `1px solid ${alpha(NEON, 0.28)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: NEON, boxShadow: `0 0 10px ${alpha(NEON, 0.2)}`,
          }}>
            <EventIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {/* Event name — bright primary text */}
            <Typography sx={{
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
              fontSize: '0.875rem', color: TEXT_PRIMARY,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {event.name}
            </Typography>
            {/* Remark — muted secondary */}
            {event.remark && (
              <Typography sx={{
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem',
                color: TEXT_SECONDARY,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {event.remark}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      icon: CategoryIcon,
      minWidth: 140,
      render: (event) => (
        <NeonChip label={event.type} neon={TYPE_NEON[event.type] || alpha(NEON, 0.45)} />
      ),
    },
    {
      key: 'start_date',
      label: 'Date & Time',
      icon: DateRangeIcon,
      minWidth: 200,
      render: (event) => (
        <Box>
          {/* Date range — clearly readable */}
          <Typography sx={{
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
            fontSize: '0.84rem', color: TEXT_PRIMARY,
          }}>
            {dayjs(event.start_date).format('MMM DD, YYYY')} – {dayjs(event.end_date).format('MMM DD, YYYY')}
          </Typography>
          {/* Time — mono, muted */}
          <Typography sx={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem',
            color: TEXT_MONO, letterSpacing: 0.5, mt: 0.25,
          }}>
            {formatEventTimeRange(event.start_time, event.end_time)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'venue',
      label: 'Venue',
      icon: LocationIcon,
      minWidth: 160,
      render: (event) => (
        <Typography sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem',
          color: TEXT_PRIMARY, fontWeight: 440,
        }}>
          {event.venue || '—'}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      minWidth: 140,
      render: (event) => {
        const icon =
          event.status?.toLowerCase() === 'completed'   ? <CheckCircleIcon />   :
          event.status?.toLowerCase() === 'in progress' ? <HourglassEmptyIcon /> :
          <ScheduleIcon />;
        return (
          <NeonChip
            label={event.status}
            neon={STATUS_NEON[event.status] || alpha(NEON, 0.45)}
            icon={icon}
          />
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 140,
      render: (event) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View Event" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); navigate(`/events/view/${event.id}`); }}
              size="small"
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canModifyEvent(currentUser, event) && (
            <>
              <Tooltip title="Edit Event" arrow componentsProps={{ tooltip: { sx: { fontFamily: "'DM Sans', sans-serif", background: alpha(SURFACE_MID, 0.95), border: `1px solid ${alpha('#F59E0B', 0.3)}` } } }}>
                <IconButton size="small"
                  onClick={(e) => { e.stopPropagation(); navigate(`/events/edit/${event.id}`); }}
                  sx={{ color: alpha('#F59E0B', 0.8), borderRadius: '7px', transition: 'all 0.18s ease', '&:hover': { bgcolor: 'rgba(245,158,11,0.1)', color: '#F59E0B', boxShadow: '0 0 10px rgba(245,158,11,0.25)' } }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canCancelEvent(currentUser, event) && (
                <Tooltip title="Cancel Event" arrow>
                  <IconButton size="small"
                    onClick={(e) => { e.stopPropagation(); handleCancel(event); }}
                    sx={{ color: 'rgba(239,68,68,0.75)', borderRadius: '7px', transition: 'all 0.18s ease', '&:hover': { bgcolor: 'rgba(239,68,68,0.1)', color: '#ef4444', boxShadow: '0 0 10px rgba(239,68,68,0.25)' } }}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      ),
    },
  ];

  if (!currentUser) return null;
  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading size={56} message="Loading events list" variant="primary" />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Header
        title="Event Management"
        subtitle="Manage and track all events and activities"
        icon={EventSidebarIcon}
        actionButton={
          <Button
            variant="contained" size="large" startIcon={<AddIcon />}
            onClick={() => navigate('/events/create')}
            disabled={!canCreateEvent(currentUser)}
            sx={{
              fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
              fontSize: '0.875rem', borderRadius: '10px', px: 3, py: 1.5,
              bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
              transition: 'all 0.22s ease',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', transform: 'scale(1.02)' },
            }}
          >
            Add Event
          </Button>
        }
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha(NEON3, 0.12),
          border: `1px solid ${alpha(NEON3, 0.35)}`,
          boxShadow: '0 4px 20px rgba(34,197,94,0.12)',
          color: alpha(NEON3, 0.95),
          mb: 3,
        }}
      />

      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />
      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={(newFilters) => {
          const nextActive =
            newFilters.status === 'In Progress' ? 'in-progress' :
            newFilters.status === 'Completed' ? 'completed' :
            newFilters.date_range === 'upcoming' ? 'upcoming' :
            'all';
          setActiveStatFilter(nextActive);
          setFilters(newFilters);
          setPage(0);
        }}
        onClearFilters={() => {
          setFilters({});
          setActiveStatFilter('all');
          setSearchTerm('');
          setPage(0);
        }}
        filterOptions={filterOptions}
        searchPlaceholder="Search by event name, venue, or remark..."
      />
      <ListBulkToolbar
        config={EVENT_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: EVENT_BULK_CONFIG.endpoint,
          setItems: setEvents,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'event(s)',
          refetch: fetchEvents,
          setError,
        })}
        onDeleteSuccess={handleBulkCancel}
      />



      <TableList
        columns={columns}
        data={filteredEvents}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        {...bulkTableProps(EVENT_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)}
        loading={loading}
        emptyState={{
          icon: <EventIcon />,
          title: 'No events found',
          description: searchTerm || Object.keys(filters).length > 0
            ? 'Try adjusting your filters to see more events.'
            : 'Get started by creating your first event.'
        }}
        onRowClick={(event) => navigate(`/events/view/${event.id}`)}
      />

      <ConfirmDeleteDialog
        open={cancelDialogOpen}
        onClose={() => { setCancelDialogOpen(false); setSelectedEvent(null); }}
        onConfirm={confirmCancel}
        loading={cancelLoading}
        title="Cancel Event"
        itemType="event"
        itemName={selectedEvent?.name}
        description="The event will be marked as Cancelled and remain in the system for records."
        confirmLabel="Cancel Event"
      />
    </Container>
  );
};

export default EventList;
